import { FormEvent, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { Check } from 'baseui/icon'
import { useSnackbar } from 'baseui/snackbar'

import { selectWebsalesUsersManagementSelected } from '../WebsalesUsersManagementSelectors'
import { setSelectedWebsalesUser } from '../WebsalesUsersManagementSlice'
import { websalesUsersManagementActions } from '../WebsalesUsersManagementSagas'
import { SelectedWebsalesUser, WebsalesUserFormErrors } from '../types'

const NO_ORIGIN = ''
const WEBSALES_USER_ORIGINS = ['myselectra', 'comparator', 'pulse'] as const

export type WebsalesUserOrigin = (typeof WEBSALES_USER_ORIGINS)[number]

interface OriginSelectParams {
  value: { id: string; label: string }[]
}

interface UseAddEditModalParams {
  setIsOpen: (open: boolean) => void
}

interface UseAddEditModalReturn {
  t: TFunction
  formErrors: WebsalesUserFormErrors
  selectedWebsalesUser: SelectedWebsalesUser
  isEdit: boolean
  originOptions: Record<string, string>
  selectedOrigin: string
  selectOrigin: (params: OriginSelectParams) => void
  updateSelectedWebsalesUser: (websalesUserInformation: Partial<SelectedWebsalesUser>) => void
  submitHandler: (event: FormEvent<HTMLFormElement>) => void
}

const useAddEditModal = ({ setIsOpen }: UseAddEditModalParams): UseAddEditModalReturn => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { enqueue } = useSnackbar()
  const [formErrors, setFormErrors] = useState<WebsalesUserFormErrors>({})
  const selectedWebsalesUser = useSelector(selectWebsalesUsersManagementSelected)
  const isEdit = !!selectedWebsalesUser.id
  const selectedOrigin = selectedWebsalesUser.origin ?? NO_ORIGIN
  const originOptions = WEBSALES_USER_ORIGINS.reduce<Record<string, string>>(
    (options, origin) => {
      options[origin] = t(`websalesUsersManagement:origins.${origin}`)
      return options
    },
    { [NO_ORIGIN]: t('websalesUsersManagement:origins.none') },
  )

  const notifySuccess = (message?: string) => {
    setIsOpen(false)
    const fallback = isEdit
      ? t('websalesUsersManagement:success_update', 'Compte websales mis à jour')
      : t('websalesUsersManagement:success_create', 'Compte websales créé')
    enqueue({
      message: message || fallback,
      startEnhancer: ({ size }: { size: number }) => <Check size={size} />,
    })
  }

  const updateSelectedWebsalesUser = (websalesUserInformation: Partial<SelectedWebsalesUser>) => {
    dispatch(setSelectedWebsalesUser({ ...selectedWebsalesUser, ...websalesUserInformation }))
  }

  const selectOrigin = (params: OriginSelectParams) => {
    const id = params.value[0]?.id
    updateSelectedWebsalesUser({ origin: WEBSALES_USER_ORIGINS.find(origin => origin === id) ?? null })
  }

  const submitHandler = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormErrors({})
    const { id = 0, username, password, is_active, origin } = selectedWebsalesUser

    if (id) {
      dispatch({
        type: websalesUsersManagementActions.UPDATE_WEBSALES_USER,
        websalesUserData: { id, username, is_active, origin },
        enqueue,
        onSuccess: notifySuccess,
        setFormErrors,
      })
      return
    }

    dispatch({
      type: websalesUsersManagementActions.CREATE_WEBSALES_USER,
      websalesUserData: { username, password, origin },
      enqueue,
      onSuccess: notifySuccess,
      setFormErrors,
    })
  }

  return {
    t,
    formErrors,
    selectedWebsalesUser,
    isEdit,
    originOptions,
    selectedOrigin,
    selectOrigin,
    updateSelectedWebsalesUser,
    submitHandler,
  }
}

export default useAddEditModal
